import type { IconName } from '../components/Icon'
import { APP_SETTINGS } from '../config/appSettings'
import type { Note } from '../domain/note'

/**
 * Quiet by default (docs/PRODUCT.md and apps/app/DESIGN.md, "Quiet by default"): sync runs in the background and nothing about it is
 * shown unless it is confirmed wrong. That is a sync, restore or unsent change that has taken far longer than expected (`stallIndicatorMs`),
 * or several failed attempts over a grace period (`errorIndicator*`). Routine progress, a short wait, or being offline with everything
 * saved on this device needs no attention, so it shows nothing.
 */
export const SYNC_STALL_MS = APP_SETTINGS.sync.stallIndicatorMs

export type BackgroundSyncState = 'idle' | 'syncing' | 'error'
export type AccountRestorePhase = 'idle' | 'restoring' | 'ready' | 'error'

export interface ContinuityStatus {
  icon: IconName
  label: string
  tone: 'error' | 'offline' | 'progress'
}

export interface SyncFailureState {
  consecutiveFailures: number
  firstFailureAt: number
  lastFailureAt: number
  message: string
}

/**
 * A note returned by the repository is already durable in IndexedDB. Its sync state describes
 * the remote copy only and must never be promoted to a local-storage error.
 */
export function storedNoteSaveState(_note: Pick<Note, 'localSyncState'>): 'local' {
  return 'local'
}

export function nextSyncFailureState(
  previous: SyncFailureState | null,
  message: string,
  now = Date.now(),
): SyncFailureState {
  return {
    consecutiveFailures: (previous?.consecutiveFailures ?? 0) + 1,
    firstFailureAt: previous?.firstFailureAt ?? now,
    lastFailureAt: now,
    message,
  }
}

export function shouldExposeSyncFailure(
  failure: SyncFailureState,
  now = Date.now(),
): boolean {
  return failure.consecutiveFailures >= APP_SETTINGS.sync.errorIndicatorMinFailedAttempts
    && now - failure.firstFailureAt >= APP_SETTINGS.sync.errorIndicatorGraceMs
}

interface ContinuityStatusInput {
  authenticated: boolean
  online: boolean
  state: BackgroundSyncState
  syncAvailable: boolean
  syncError: string | null
  restorePhase?: AccountRestorePhase
  /** Unsent changes have waited far longer than expected (scheduleStallNotice). */
  pendingWorkVisible?: boolean
  /** The account restore has run far longer than expected (scheduleStallNotice). */
  restoreStalled?: boolean
}

/** Calls `show` once work has run far longer than expected; the returned cancel keeps a sync that finishes in time silent. */
export function scheduleStallNotice(show: () => void): () => void {
  const timer = globalThis.setTimeout(show, SYNC_STALL_MS)
  return () => globalThis.clearTimeout(timer)
}

export function getComposerContinuityStatus(
  status: ContinuityStatus | null,
  restorePhase: AccountRestorePhase,
): ContinuityStatus | null {
  if (restorePhase === 'restoring' && status?.tone === 'progress') return null
  return status
}

export function getContinuityStatus({
  authenticated,
  online,
  state,
  syncAvailable,
  syncError,
  restorePhase = 'idle',
  pendingWorkVisible = false,
  restoreStalled = false,
}: ContinuityStatusInput): ContinuityStatus | null {
  // Offline alone needs no attention (everything is saved here); offline with changes unsent for long does.
  if (!online) return pendingWorkVisible ? { icon: 'clock', label: 'Offline, not synced yet', tone: 'offline' } : null
  if (!syncAvailable || !authenticated) return null
  if (restorePhase === 'error' || state === 'error' || syncError) {
    return { icon: 'cloud', label: 'Sync issue', tone: 'error' }
  }
  if (restorePhase === 'restoring' && restoreStalled) {
    return { icon: 'cloud', label: 'Still restoring', tone: 'progress' }
  }
  // `syncing` is only set once a cycle has run far longer than expected (App.tsx, scheduleStallNotice).
  if (state === 'syncing') {
    return { icon: 'cloud', label: 'Still syncing', tone: 'progress' }
  }
  if (pendingWorkVisible) {
    return { icon: 'clock', label: 'Not synced yet', tone: 'offline' }
  }
  return null
}
